import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth, useTheme } from '../context/AppContext'
import { useIsMobile } from '../hooks/useIsMobile'
import toast from 'react-hot-toast'
import { Eye, EyeOff, Sun, Moon, ArrowRight } from 'lucide-react'
import logo from '../assets/logo.png'
import api from '../services/api'

export default function ForcePasswordChangePage() {
  const { user, refreshMe } = useAuth()
  const { toggle, isDark } = useTheme()
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const [form, setForm] = useState({ oldPassword: '', newPassword: '', confirmPassword: '' })
  const [show, setShow] = useState(false)
  const [error, setError] = useState('')
  const [focused, setFocused] = useState('')
  const [saving, setSaving] = useState(false)

  const inputFontSize = isMobile ? 16 : 14

  const handleSubmit = async e => {
    e.preventDefault()
    setError('')

    if (!form.oldPassword.trim()) { setError('Current password is required'); return }
    if (form.newPassword.length < 8) { setError('New password must be at least 8 characters'); return }
    if (form.newPassword !== form.confirmPassword) { setError('Passwords do not match'); return }

    setSaving(true)
    try {
      await api.post('/auth/change-password', {
        old_password: form.oldPassword,
        new_password: form.newPassword,
      })
      toast.success('Password updated')
      const refreshed = await refreshMe()

      const isAdmin = refreshed?.is_superuser || refreshed?.is_staff
      const isPartner = !isAdmin && !!refreshed?.company_id
      const target =
        isPartner ? '/partner-dashboard' :
        isAdmin   ? '/admin-leads' :
        '/'
      navigate(target, { replace: true })
    } catch (err) {
      const detail = err.response?.data?.detail
      setError(typeof detail === 'string' ? detail : 'Could not update password')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--bg-base)',
      position: 'relative',
      overflow: 'hidden',
    }}>
      <button
        onClick={toggle}
        style={{
          position: 'absolute', top: 24, right: 24,
          width: 38, height: 38, borderRadius: '50%',
          background: 'var(--bg-surface)',
          border: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', color: 'var(--text-secondary)',
          transition: 'all var(--transition)',
          boxShadow: 'var(--shadow-sm)',
        }}
      >
        {isDark ? <Sun size={15} /> : <Moon size={15} />}
      </button>

      <div style={{
        width: 'min(400px, 92vw)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        animation: 'loginEnter 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
      }}>
        <div style={{ marginBottom: 28, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <img
            src={logo}
            alt="Logo"
            style={{
              height: 52, width: 'auto', objectFit: 'contain',
              filter: isDark ? 'brightness(0) invert(1)' : 'none',
              transition: 'filter 0.3s ease',
            }}
          />
        </div>

        <div style={{
          width: '100%',
          background: 'var(--bg-surface)',
          border: '1px solid var(--border)',
          borderRadius: 20,
          padding: isMobile ? '28px 22px 24px' : '36px 36px 32px',
          boxShadow: isDark
            ? '0 0 0 1px rgba(255,255,255,0.04), 0 24px 64px rgba(0,0,0,0.4)'
            : '0 0 0 1px rgba(0,0,0,0.04), 0 24px 64px rgba(0,0,0,0.08)',
        }}>
          <div style={{ marginBottom: 18 }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: 'var(--text-primary)' }}>
              Set a new password
            </h2>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
              {user?.email ? `Signed in as ${user.email}. ` : ''}
              You must set a new password before continuing.
            </p>
          </div>

          <form onSubmit={handleSubmit} noValidate>
            {[
              { key: 'oldPassword', label: 'Current (temporary) password', autoComplete: 'current-password' },
              { key: 'newPassword', label: 'New password', autoComplete: 'new-password' },
              { key: 'confirmPassword', label: 'Confirm new password', autoComplete: 'new-password' },
            ].map(({ key, label, autoComplete }, i) => (
              <div key={key} style={{ marginBottom: i === 2 ? 20 : 14 }}>
                <div style={{
                  position: 'relative',
                  borderRadius: 12,
                  border: `1px solid ${focused === key ? 'var(--border-strong)' : 'var(--border)'}`,
                  background: 'var(--bg-raised)',
                  transition: 'border-color var(--transition), box-shadow var(--transition)',
                  boxShadow: focused === key
                    ? isDark ? '0 0 0 3px rgba(255,255,255,0.06)' : '0 0 0 3px rgba(0,0,0,0.05)'
                    : 'none',
                }}>
                  <label style={{
                    position: 'absolute', top: 9, left: 14,
                    fontSize: 10, fontWeight: 600, letterSpacing: '0.06em',
                    textTransform: 'uppercase', color: 'var(--text-muted)',
                    fontFamily: 'var(--font-display)',
                    pointerEvents: 'none',
                  }}>
                    {label}
                  </label>
                  <input
                    type={show ? 'text' : 'password'}
                    autoComplete={autoComplete}
                    aria-label={label}
                    required
                    value={form[key]}
                    onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                    onFocus={() => setFocused(key)}
                    onBlur={() => setFocused('')}
                    style={{
                      width: '100%', background: 'none', border: 'none', outline: 'none',
                      padding: '28px 44px 10px 14px',
                      fontSize: inputFontSize, color: 'var(--text-primary)',
                      fontFamily: 'var(--font-body)',
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShow(s => !s)}
                    aria-label={show ? 'Hide passwords' : 'Show passwords'}
                    style={{
                      position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: 'var(--text-muted)', padding: 4, display: 'flex',
                    }}
                  >
                    {show ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>
            ))}

            {error && (
              <div role="alert" style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: 'var(--red-bg)', color: 'var(--red)',
                fontSize: 12, padding: '10px 14px',
                borderRadius: 10, marginBottom: 16,
                border: '1px solid rgba(185,28,28,0.15)',
              }}>
                <span style={{ fontSize: 15 }}>⚠</span>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={saving}
              style={{
                width: '100%',
                padding: '13px 20px',
                background: 'var(--accent)',
                color: 'var(--accent-fg)',
                border: 'none',
                borderRadius: 12,
                fontSize: 14,
                fontWeight: 600,
                fontFamily: 'var(--font-display)',
                cursor: saving ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                opacity: saving ? 0.7 : 1,
                letterSpacing: '0.01em',
              }}
            >
              {saving
                ? <div className="spinner" style={{ borderColor: 'rgba(255,255,255,0.3)', borderTopColor: '#fff' }} />
                : <>Update password <ArrowRight size={15} /></>
              }
            </button>
          </form>
        </div>
      </div>

      <style>{`
        @keyframes loginEnter {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  )
}
